import { postOrgPlaybooksEditSessionsByEditSessionIdCommitMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { NewVersionAction, OffsetPaginatedPlaybookVersionNoGraph, Playbook, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { type QueryObserverResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import useHiddenNodeErrors from '@/components/playbook-builder/hooks/use-hidden-node-errors';
import useStore from '@/components/playbook-builder/hooks/use-store';
import { useVersion } from '@/features/playbook-version-badge';
import useInProgressEditSessionStorage from '@/hooks/use-in-progress-edit-session-storage';
import usePermissions from '@/hooks/use-permissions';
import useUserSession from '@/hooks/use-user-session';
import { usePlaybook } from '../../hooks/use-playbook';
import PublishActions from './publish-actions';
type PublishActionsContainerProps = {
  playbook: Playbook;
  playbookVersion: PlaybookVersionNoGraph;
  playbookVersions: PlaybookVersionNoGraph[];
  onRefetch: () => Promise<QueryObserverResult<OffsetPaginatedPlaybookVersionNoGraph, Error>>;
  onDeploy: (versionId?: string) => void;
  onViewDeployment: () => void;
};
const PublishActionsContainer = ({
  playbook,
  playbookVersion,
  playbookVersions,
  onRefetch,
  onDeploy,
  onViewDeployment
}: PublishActionsContainerProps) => {
  const store = useStore();
  const {
    hasHiddenErrors,
    reveal: revealHiddenErrors
  } = useHiddenNodeErrors();
  const {
    data
  } = useUserSession();
  const {
    hasPermission
  } = usePermissions();
  const commitMutation = useMutation(postOrgPlaybooksEditSessionsByEditSessionIdCommitMutation());
  const [, setVersion] = useQueryState('version');
  const errorToast = useRequestErrorToast();
  const {
    status
  } = useVersion(playbookVersion);
  const {
    selectedVersion
  } = usePlaybook(playbook.id);
  const queryClient = useQueryClient();
  const {
    clearEditSessionId
  } = useInProgressEditSessionStorage(playbook.id);
  const handleSubmit = async (payload: {
    action: NewVersionAction;
    versionDescription?: string;
    skipValidation?: boolean;
  }): Promise<{
    versionId?: string;
  }> => {
    const editSessionId = store.data.sessionId;
    if (!editSessionId) {
      throw new Error('No session ID found');
    }
    try {
      const response = await commitMutation.mutateAsync({
        path: {
          editSessionId
        },
        body: {
          expectedActiveDeploymentId: playbook.activeDeployment.id,
          ...payload
        }
      });
      const {
        data: data_0
      } = await onRefetch();
      const versionId = data_0?.data[0].id;
      if (versionId) {
        setVersion(versionId);
        store.reset({
          obc: response
        });
      }
      queryClient.invalidateQueries();
      clearEditSessionId(playbookVersion.id);
      return {
        versionId
      };
    } catch (error) {
      errorToast(error);
      return {};
    }
  };
  const handleSubmitAndDeploy = async (payload_0: {
    action: NewVersionAction;
    versionDescription?: string;
    skipValidation?: boolean;
  }): Promise<{
    versionId?: string;
  }> => {
    return handleSubmit({
      ...payload_0,
      action: 'publish'
    });
  };
  const handleReset = async () => {
    store.reset(selectedVersion ? {
      obc: selectedVersion
    } : undefined);
  };
  return <PublishActions disableErrors={!!data?.isFirmEmployee} hasErrors={store.visibleBanners.some(banner => banner.type === 'error')} hasHiddenErrors={hasHiddenErrors} onRevealHiddenErrors={revealHiddenErrors} hasPermissions={hasPermission('onboarding_configuration')} isViewOnly={!store.data.canEdit} onSubmit={handleSubmit} onSubmitAndDeploy={handleSubmitAndDeploy} onDeploy={onDeploy} onReset={handleReset} onViewDeployment={onViewDeployment} activeDeployment={playbook.activeDeployment} graph={store.data.graph} playbookVersions={playbookVersions} playbook={{
    hasProdOnboarding: playbook.hasProdOnboarding,
    canPublish: status !== 'deployed' && status !== 'deprecated',
    isEditable: playbookVersion.kind !== 'bank_linking',
    isImmutable: playbookVersion.isImmutable
  }} isDeployed={status === 'deployed'} hasPendingChanges={store.data.history.hasChanges} />;
};
export default PublishActionsContainer;